/**
 * RecipesTab -- renders the Recipes list from a JournalTabState.
 *
 * Discovered recipes show ingredient names + quantities -> result name
 * with a warm panel. Undiscovered recipes show "???" with a dimmed,
 * locked appearance. Fool-vocation players see a poetic hint line
 * for undiscovered recipes (set by the dispatch builder).
 *
 * Layout: container is positioned at the journal content column origin
 * by the scene; this renderer lays out cards relative to (0, 0).
 *
 * SMA-76 — Recipe Discovery Book.
 */

import * as Phaser from 'phaser';
import {
  JOURNAL_FONT,
  JOURNAL_INK,
  JOURNAL_INK_MUTED,
  JOURNAL_FONT_SIZE,
  JOURNAL_LAYOUT,
} from './journalTheme';
import type { JournalTabState } from './journalDispatch';
import { drawPaperSlip } from '../ui/c5/paperMaterial';

/** Slip styling constants -- mirrors WhispersTab. Each recipe is a pinned
 *  paper slip on the journal spread (C5 "Indigo & Ink"). */
const SLIP_ALPHA = 0.96;
const PANEL_PAD_X = 12;
const PANEL_PAD_Y = 8;
const PANEL_GAP = 10;

/** Dimmed alpha for undiscovered recipes. */
const UNDISCOVERED_ALPHA = 0.4;

/** Content width available inside the panel. */
const INNER_WIDTH =
  JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2 - PANEL_PAD_X * 2;

export function renderRecipesTab(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  state: JournalTabState,
): void {
  container.removeAll(true);

  if (state.lines.length === 0) {
    const text = scene.add.text(0, 24, state.emptyMessage, {
      fontFamily: JOURNAL_FONT,
      fontSize: JOURNAL_FONT_SIZE.EMPTY_STATE,
      color: JOURNAL_INK_MUTED,
      fontStyle: 'italic',
      wordWrap: {
        width: JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2,
      },
    });
    container.add(text);
    return;
  }

  let y = 0;
  for (const line of state.lines) {
    const isUndiscovered = line.footnote === 'undiscovered';
    const cardContainer = scene.add.container(0, y);
    container.add(cardContainer);

    let innerY = PANEL_PAD_Y;

    // Heading: recipe name or "???"
    if (line.heading) {
      const heading = scene.add.text(PANEL_PAD_X, innerY, line.heading, {
        fontFamily: JOURNAL_FONT,
        fontSize: JOURNAL_FONT_SIZE.HEADING,
        color: isUndiscovered ? JOURNAL_INK_MUTED : JOURNAL_INK,
        fontStyle: isUndiscovered ? 'italic' : 'normal',
      });
      if (isUndiscovered) heading.setAlpha(UNDISCOVERED_ALPHA);
      cardContainer.add(heading);
      innerY += heading.height + 4;
    }

    // Body: ingredient line or "???" / Fool hint
    const body = scene.add.text(PANEL_PAD_X, innerY, line.body, {
      fontFamily: JOURNAL_FONT,
      fontSize: JOURNAL_FONT_SIZE.BODY,
      color: isUndiscovered ? JOURNAL_INK_MUTED : JOURNAL_INK,
      fontStyle: isUndiscovered ? 'italic' : 'normal',
      wordWrap: { width: INNER_WIDTH },
    });
    if (isUndiscovered) body.setAlpha(UNDISCOVERED_ALPHA);
    cardContainer.add(body);
    innerY += body.height + 4;

    // Footnote: recipe description (discovered) or nothing useful (undiscovered)
    if (line.footnote && !isUndiscovered) {
      const footnote = scene.add.text(PANEL_PAD_X, innerY, line.footnote, {
        fontFamily: JOURNAL_FONT,
        fontSize: JOURNAL_FONT_SIZE.FOOTNOTE,
        color: JOURNAL_INK_MUTED,
        fontStyle: 'italic',
        wordWrap: { width: INNER_WIDTH },
      });
      cardContainer.add(footnote);
      innerY += footnote.height + 2;
    }

    innerY += PANEL_PAD_Y;

    // Background panel
    const panelW = JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2;
    const panelH = innerY;
    const bg = scene.add.graphics();
    // Undiscovered recipes are paper the traveler has not written on yet:
    // the same slip, only fainter.
    drawPaperSlip(bg, 0, 0, panelW, panelH, {
      radius: 3,
      alpha: isUndiscovered ? SLIP_ALPHA * 0.45 : SLIP_ALPHA,
      shadow: isUndiscovered ? 0 : 3,
    });
    cardContainer.addAt(bg, 0);

    y += panelH + PANEL_GAP;
  }
}
